import React, { useState, useMemo } from 'react';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useAlbum } from '../contexts/AlbumContext';
import { HistoryControls } from '../components/features/history/HistoryControls';
import { HistoryTitle } from '../components/features/history/HistoryTitle';
import { AlbumListView } from '../components/features/history/AlbumListView';
import { AlbumGridView } from '../components/features/history/AlbumGridView';
import { formatAddedDate } from '../utils/formatters';

const preventOrphanWords = (text) => {
  if (!text) return '';
  const words = text.split(' ');
  if (words.length <= 1) return text;
  const lastWord = words.pop();
  return `${words.join(' ')}\u00A0${lastWord}`;
};

const History = () => {
  const { history, loading, error } = useAlbum();
  const [viewMode, setViewMode] = useState('grid');
  const [sortOrder, setSortOrder] = useState('newest');
  const [ratingFilter, setRatingFilter] = useState(null);
  const [expandedContent, setExpandedContent] = useState(null);

  const handleContentToggle = (albumKey, contentType) => {
    setExpandedContent(prev => {
      // If clicking the active content type, close it
      if (prev?.albumKey === albumKey && prev?.type === contentType) {
        return null;
      }
      // If switching content type for the same album or opening new content
      return { albumKey, type: contentType };
    });
  };

  const displayedHistory = useMemo(() => {
    let filtered = [...history];
    if (ratingFilter !== null) {
      filtered = filtered.filter(item =>
        ratingFilter === 0 ? !item.rating : item.rating === ratingFilter
      );
    }
    return sortOrder === 'newest' ? [...filtered].reverse() : filtered;
  }, [history, ratingFilter, sortOrder]);

  const albumNumbers = useMemo(() => {
    return new Map(
      [...history].map((item, index) => [
        item.album.name + item.generatedAt,
        index + 1
      ])
    );
  }, [history]);

  const getColumnsForViewport = () => {
    if (window.matchMedia('(min-width: 1280px)').matches) return 5; // xl
    if (window.matchMedia('(min-width: 1024px)').matches) return 4; // lg
    if (window.matchMedia('(min-width: 640px)').matches) return 3;  // sm
    return 2; // default
  };

  const [columnsPerRow, setColumnsPerRow] = useState(getColumnsForViewport());

  React.useEffect(() => {
    const updateColumns = () => {
      setColumnsPerRow(getColumnsForViewport());
    };

    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  if (loading) return <LoadingView />;
  if (error) return <ErrorView error={error} />;
  if (!history || history.length === 0) return <div className="p-4">No albums found</div>;

  const renderGridItems = () => {
    const items = [];
    displayedHistory.forEach((item, index) => {
      const albumKey = item.album.name + item.generatedAt;
      const albumNumber = albumNumbers.get(albumKey);
      const isExpanded = expandedContent?.albumKey === albumKey;
      const activeContent = isExpanded ? expandedContent.type : null;
      const row = Math.floor(index / columnsPerRow);
      const column = index % columnsPerRow;

      items.push(
        <div
          key={albumKey}
          style={{ gridRow: row * 2 + 1 }}
          className="group grid grid-rows-[auto,1fr] bg-amber-50 rounded-2xl overflow-clip h-full"
        >
          <AlbumGridView
            item={item}
            albumNumber={albumNumber}
            preventOrphanWords={preventOrphanWords}
            activeContent={activeContent}
            onGenresClick={(e) => {
              e.stopPropagation();
              handleContentToggle(albumKey, 'genres');
            }}
            onReviewClick={(e) => {
              e.stopPropagation();
              handleContentToggle(albumKey, 'review');
            }}
          />
        </div>
      );

      // Render the content row if expanded
      if (isExpanded) {
        const tipLeftPosition = `${(column + 0.5) * (100 / columnsPerRow)}%`;
        items.push(
          <div
            key={`content-${albumKey}`}
            style={{ gridRow: row * 2 + 2 }}
            className="relative my-2 md:p-8 p-4 bg-amber-50 rounded-2xl col-span-full"
          >
            <div
              className="absolute -top-2 z-0 w-6 h-6 rotate-45 rounded-md bg-amber-50 transform -translate-x-1/2"
              style={{ left: tipLeftPosition }}
            />
            {activeContent === 'genres' ? (
              <div className="flex flex-wrap justify-center items-center gap-3 relative z-10">
                <div className="flex flex-row gap-1">
                  {item.album.genres?.map(genre => (
                    <span key={genre} className="px-2 rounded-full bg-black/5">
                      {genre}
                    </span>
                  )) || 'No genres available'}
                </div>
              </div>
            ) : (
              <div className="flex flex-row flex-wrap justify-between items-baseline gap-2 relative z-10">

                <div className="flex flex-row flex-wrap items-center w-full gap-1">

                  <div className="flex flex-row flex-1 gap-1">

                    <a
                      className="mr-3 hover:scale-110 transition duration-100 ease-in-out opacity-30 hover:opacity-100"
                      href={item.album.globalReviewsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div className="group flex flex-row sm:h-6 h-4 justify-center items-center cursor-pointer text-center pangram-sans-bold">
                        <span className="material-symbols-outlined text-2xl">language</span>
                      </div>
                    </a>

                    {/* <div className="mx-1 pangram-sans-bold opacity-10">/</div> */}

                    {item.album.genres?.map(genre => (
                      <span key={genre} className="px-2 rounded-full bg-black/5">
                        {genre}
                      </span>
                    )) || 'No genres available'}
                  </div>

                  <div
                    className="flex h-full aspect-square items-center justify-center cursor-pointer"
                    onClick={() => setExpandedContent(null)}
                  >
                    <span className="material-symbols-outlined text-xl opacity-50">close</span>
                  </div>

                </div>

                <div>
                  {item.review || 'No review available'}
                </div>

                <div className="text-sm opacity-50">
                  {formatAddedDate(item.generatedAt)}
                </div>

              </div>
            )}
          </div>
        );
      }
    });

    return items;
  };

  return (
    <div className="flex flex-col flex-1 md:text-base text-sm bg-neutral-800">
      <HistoryControls
        viewMode={viewMode}
        setViewMode={setViewMode}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        ratingFilter={ratingFilter}
        setRatingFilter={setRatingFilter}
      />

      <HistoryTitle displayedHistory={displayedHistory} ratingFilter={ratingFilter} />

      <div className={viewMode === 'grid'
        ? 'grid auto-rows-min grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:px-4 py-4 gap-y-2 gap-x-4'
        : 'flex flex-col sm:px-4 py-4 gap-4'
      }>
        {viewMode === 'grid' ? renderGridItems() : displayedHistory.map((item) => {
          const albumKey = item.album.name + item.generatedAt;
          const albumNumber = albumNumbers.get(albumKey);

          return (
            <div key={albumKey} className="group relative bg-amber-50 rounded-2xl overflow-clip">
              <AlbumListView
                item={item}
                albumNumber={albumNumber}
                preventOrphanWords={preventOrphanWords}
              />
            </div>
          );
        })}
      </div>

      <div className="flex w-full items-center justify-center pt-16 pb-24 text-amber-50">
        <span className="material-symbols-outlined xl:text-[8rem] lg:text-[6.5rem] md:text-[4.5rem] text-[3rem]">
          electric_bolt
        </span>
      </div>
    </div>
  );
};

const LoadingView = () => (
  <div className="flex items-center justify-center w-full min-h-screen">
    <LoadingSpinner />
  </div>
);

const ErrorView = ({ error }) => (
  <div className="flex flex-col gap-4 w-full p-16 justify-center items-center text-2xl text-red-500">
    <span className="material-symbols-outlined text-9xl">warning</span>
    {error}
    {error.includes('Rate limit') && (
      <p className="text-sm text-amber-50">Note: API is limited to 3 requests per minute.</p>
    )}
  </div>
);

export default History;